import { useTranslation } from "react-i18next";

import { useLastMeasurements } from "~/hooks";
import {
  BloodGlucoseCard,
  BloodOxygenSaturationCard,
  BloodPressureCard,
  BodyTemperatureCard,
  BodyWeightCard,
  HealthDetails,
  HeartRateCard,
  HeartRateVariabilityCard,
  RespiratoryRateCard,
  StepsCard,
} from "./components";

interface HealthProps {
  patientId: number;
}

// Default measurements when API data is not available
const DEFAULT_MEASUREMENTS = {
  blood_glucose: null,
  blood_oxygen_saturation: null,
  blood_pressure_systolic: null,
  blood_pressure_diastolic: null,
  body_temperature: null,
  respiratory_rate: null,
  body_weight: null,
  heart_rate: null,
  heart_rate_variability: null,
  walking_steps: null,
} as const;

export const Health = ({ patientId }: HealthProps) => {
  const { t } = useTranslation();

  const {
    data: measurements,
    isLoading,
    error,
  } = useLastMeasurements(patientId);

  // Show loading state
  if (isLoading) {
    return (
      <HealthDetails.Root>
        <HealthDetails.Title>{t("patient_detail.health")}</HealthDetails.Title>
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-400">Loading...</div>
        </div>
      </HealthDetails.Root>
    );
  }

  // Show error state
  if (error) {
    return (
      <HealthDetails.Root>
        <HealthDetails.Title>{t("patient_detail.health")}</HealthDetails.Title>
        <div className="flex items-center justify-center p-8">
          <div className="text-red-400">Error loading data</div>
        </div>
      </HealthDetails.Root>
    );
  }

  // Use API data or fallback to defaults
  const healthData = measurements ?? DEFAULT_MEASUREMENTS;

  return (
    <HealthDetails.Root>
      <HealthDetails.Title className="mb-6">
        {t("patient_detail.health")}
      </HealthDetails.Title>

      {/* Automatic Measurements Section */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-200">
          Automatic Measurements
        </h3>
        <HealthDetails.CardsHolder>
          <BodyTemperatureCard
            measurement={healthData.body_temperature}
            patientId={patientId}
          />
          <HeartRateVariabilityCard
            measurement={healthData.heart_rate_variability}
          />
          <HeartRateCard
            measurement={healthData.heart_rate}
            patientId={patientId}
          />
          <BloodOxygenSaturationCard
            measurement={healthData.blood_oxygen_saturation}
            patientId={patientId}
          />
          <RespiratoryRateCard measurement={healthData.respiratory_rate} />
          <StepsCard
            measurement={healthData.walking_steps}
            patientId={patientId}
          />
        </HealthDetails.CardsHolder>
      </div>

      {/* Manual Measurements Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-200">
          Manual Measurements
        </h3>
        <HealthDetails.CardsHolder>
          <BloodPressureCard
            systolicMeasurement={healthData.blood_pressure_systolic}
            diastolicMeasurement={healthData.blood_pressure_diastolic}
            patientId={patientId}
          />
          <BodyWeightCard
            measurement={healthData.body_weight}
            patientId={patientId}
          />
          <BloodGlucoseCard
            measurement={healthData.blood_glucose}
            patientId={patientId}
          />
        </HealthDetails.CardsHolder>
      </div>
    </HealthDetails.Root>
  );
};
